<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import PipelineNode from '@/components/PipelineNode.vue'
import { useProjects } from '@/composables/useProjects'
import { useJobs } from '@/composables/useJobs'
import { useSites } from '@/composables/useSites'

type PipelineState = 'complete' | 'not-started' | 'active'

interface ProjectLike {
  id?: number
  name: string
  status?: string | null
  visible?: boolean | null
  area_of_interest?: number | null
  area_of_interest_name?: string | null
  area_of_interest_visible?: boolean | null
  parameters?: Record<string, unknown> | null
  download_batch_offsets?: {
    batches?: Array<{ complete?: boolean | number | string | null }>
  } | null
}

interface SiteLike {
  id?: number
  name?: string
  visible?: boolean | null
}

interface JobLike {
  project?: number
  completed?: boolean
}

interface PipelineRow {
  site: string
  selectedProjectId: number | null
  workflowSubtitle: string
  batchesSubtitle: string
  jobsSubtitle: string
  workflowsFinished: number
  workflowsTotal: number
  processingCompleted: number
  processingTotal: number
  outstandingJobCount: number
  job: {
    name: string
    completed: number
    total: number
  }
  workflowState: PipelineState
  processingState: PipelineState
  jobState: PipelineState
  workflowPercent: number
  processingPercent: number
  jobPercent: number
  showWorkflowExpand: boolean
  showProcessingExpand: boolean
  showJobExpand: boolean
  showProcessing: boolean
  showJob: boolean
}

const POLL_INTERVAL_MS = 5000

const { projects, fetchProjects } = useProjects()
const { jobs, fetchJobs } = useJobs()
const { sites, fetchItems: fetchSites } = useSites()
const loading = ref(true)
const errorMessage = ref<string | null>(null)
let pollingTimer: ReturnType<typeof setInterval> | null = null

const asBool = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true'
  }
  return false
}

const isBatchComplete = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value > 0
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1'
  }
  return false
}

const toState = (completed: number, total: number): PipelineState => {
  if (total <= 0 || completed <= 0) {
    return 'not-started'
  }
  if (completed >= total) {
    return 'complete'
  }
  return 'active'
}

const toPercent = (completed: number, total: number) => {
  if (total <= 0) {
    return 0
  }
  return Math.round((completed / total) * 100)
}

const siteVisibilityById = computed(() => {
  const map = new Map<number, boolean>()
  ;(sites.value as SiteLike[]).forEach((site) => {
    if (typeof site.id === 'number' && site.visible === false) {
      map.set(site.id, false)
    }
  })
  return map
})

const siteVisibilityByName = computed(() => {
  const map = new Map<string, boolean>()
  ;(sites.value as SiteLike[]).forEach((site) => {
    const name = typeof site.name === 'string' ? site.name.trim().toLowerCase() : ''
    if (name && site.visible === false) {
      map.set(name, false)
    }
  })
  return map
})

const isProjectSiteVisible = (project: ProjectLike) => {
  if (project.visible === false || project.area_of_interest_visible === false) {
    return false
  }

  if (typeof project.area_of_interest === 'number') {
    if (siteVisibilityById.value.get(project.area_of_interest) === false) {
      return false
    }
  }

  const siteName = typeof project.area_of_interest_name === 'string'
    ? project.area_of_interest_name.trim().toLowerCase()
    : ''
  if (siteName && siteVisibilityByName.value.get(siteName) === false) {
    return false
  }

  return true
}

const renderedRows = computed<PipelineRow[]>(() => {
  const grouped = new Map<string, ProjectLike[]>()

  ;(projects.value as ProjectLike[]).forEach((project) => {
    if (!isProjectSiteVisible(project)) {
      return
    }

    const key = project.area_of_interest_name || 'Unknown Site'
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)?.push(project)
  })

  const rows = Array.from(grouped.entries()).map(([site, siteProjects]) => {
    const workflowsTotal = siteProjects.length
    const workflowsFinished = siteProjects.filter((project) =>
      asBool(project.parameters?.ongoing_monitoring)
    ).length

    const projectForBatches =
      siteProjects.find((project) => {
        const batches = project.download_batch_offsets?.batches
        return Array.isArray(batches) && batches.length > 0
      }) ?? siteProjects[0]

    const selectedProjectBatches = projectForBatches?.download_batch_offsets?.batches
    const processingTotal =
      Array.isArray(selectedProjectBatches) && selectedProjectBatches.length > 0
        ? selectedProjectBatches.length
        : 1
    const processingCompleted = Array.isArray(selectedProjectBatches)
      ? selectedProjectBatches.filter((batch) => isBatchComplete(batch.complete)).length
      : 0

    const selectedProjectId = typeof projectForBatches?.id === 'number' ? projectForBatches.id : null
    const outstandingJobsForProject = (jobs.value as JobLike[]).filter((job) => {
      if (selectedProjectId == null) {
        return false
      }
      return Number(job.project) === Number(selectedProjectId) && job.completed !== true
    })
    const outstandingJobCount = outstandingJobsForProject.length

    // Interim display convention requested: 1/N for outstanding jobs.
    const jobTotal = outstandingJobCount > 0 ? outstandingJobCount : 1
    const jobCompleted = 1

    const processingState = toState(processingCompleted, processingTotal)
    const workflowStateRaw = toState(workflowsFinished, workflowsTotal)
    const workflowState: PipelineState =
      processingState === 'active' ? 'active' : workflowStateRaw
    const jobState = toState(jobCompleted, jobTotal)

    const workflowPercent = toPercent(workflowsFinished, workflowsTotal)
    const processingPercent = toPercent(processingCompleted, processingTotal)
    const jobPercent = toPercent(jobCompleted, jobTotal)

    return {
      site,
      selectedProjectId,
      workflowSubtitle:
        selectedProjectId != null ? `Reference project #${selectedProjectId}` : 'No project selected',
      batchesSubtitle:
        selectedProjectId != null ? `From project #${selectedProjectId}` : 'No batches available',
      jobsSubtitle:
        selectedProjectId != null ? `Queue for project #${selectedProjectId}` : 'No linked project',
      workflowsFinished,
      workflowsTotal,
      processingCompleted,
      processingTotal,
      outstandingJobCount,
      job: {
        name: outstandingJobCount > 0 ? `${outstandingJobCount} outstanding jobs` : 'No outstanding jobs',
        completed: jobCompleted,
        total: jobTotal
      },
      workflowState,
      processingState,
      jobState,
      workflowPercent,
      processingPercent,
      jobPercent,
      showWorkflowExpand: workflowsTotal > 1,
      showProcessingExpand: processingTotal > 1,
      showJobExpand: jobTotal > 1,
      showProcessing: workflowState !== 'complete',
      showJob: workflowState !== 'complete' && processingState !== 'complete'
    }
  })

  return rows.sort((left, right) => {
    if (left.processingCompleted !== right.processingCompleted) {
      return right.processingCompleted - left.processingCompleted
    }

    const leftOutstandingJobs = left.outstandingJobCount
    const rightOutstandingJobs = right.outstandingJobCount

    if (leftOutstandingJobs !== rightOutstandingJobs) {
      return rightOutstandingJobs - leftOutstandingJobs
    }

    if (left.workflowPercent !== right.workflowPercent) {
      return right.workflowPercent - left.workflowPercent
    }
    if (left.processingPercent !== right.processingPercent) {
      return right.processingPercent - left.processingPercent
    }
    if (left.jobPercent !== right.jobPercent) {
      return right.jobPercent - left.jobPercent
    }
    return left.site.localeCompare(right.site)
  })
})

async function refreshPipeline() {
  try {
    errorMessage.value = null
    await Promise.all([fetchProjects(), fetchJobs(), fetchSites()])
  } catch {
    errorMessage.value = 'Could not load pipeline workflow status.'
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await refreshPipeline()

  pollingTimer = setInterval(() => {
    void refreshPipeline()
  }, POLL_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
})
</script>

<template>
  <section class="pipeline-view">
    <h2>Pipeline Overview</h2>
    <p class="pipeline-note">Derived from current projects data and queue state.</p>

    <p v-if="loading" class="status">Loading pipeline...</p>
    <p v-else-if="errorMessage" class="status status--error">{{ errorMessage }}</p>

    <section v-else class="pipeline-board" aria-label="Site pipeline board">
      <header class="pipeline-header" aria-hidden="true">
        <span class="header-cell">Site</span>
        <span class="header-cell header-cell--connector"></span>
        <span class="header-cell">Workflows</span>
        <span class="header-cell header-cell--connector"></span>
        <span class="header-cell">Batches</span>
        <span class="header-cell header-cell--connector"></span>
        <span class="header-cell">Job</span>
      </header>

      <div class="divider"></div>

      <div v-for="row in renderedRows" :key="row.site" class="pipeline-row">
        <PipelineNode :label="row.site" :state="row.workflowState" :is-site="true" />

        <div class="connector-cell">
          <span class="connector-line" aria-hidden="true"></span>
          <span class="connector-label" :style="{ '--progress': `${row.workflowPercent}%` }">
            {{ row.workflowPercent }}%
          </span>
          <span class="connector-arrow" aria-hidden="true"></span>
        </div>

        <PipelineNode
          :label="`${row.site} Workflows`"
          :subtitle="row.workflowSubtitle"
          :state="row.workflowState"
          :badge-completed="row.workflowsFinished"
          :badge-total="row.workflowsTotal"
          :show-expand="row.showWorkflowExpand"
        />

        <div class="connector-cell">
          <template v-if="row.showProcessing">
            <span class="connector-line" aria-hidden="true"></span>
            <span class="connector-label" :style="{ '--progress': `${row.processingPercent}%` }">
              {{ row.processingPercent }}%
            </span>
            <span class="connector-arrow" aria-hidden="true"></span>
          </template>
        </div>

        <template v-if="row.showProcessing">
          <PipelineNode
            label="Batches"
            :subtitle="row.batchesSubtitle"
            :state="row.processingState"
            :badge-completed="row.processingCompleted"
            :badge-total="row.processingTotal"
            :show-expand="row.showProcessingExpand"
          />
        </template>
        <template v-else>
          <PipelineNode is-placeholder />
        </template>

        <div class="connector-cell">
          <template v-if="row.showJob">
            <span class="connector-line" aria-hidden="true"></span>
            <span class="connector-label" :style="{ '--progress': `${row.jobPercent}%` }">
              {{ row.jobPercent }}%
            </span>
            <span class="connector-arrow" aria-hidden="true"></span>
          </template>
        </div>

        <template v-if="row.showJob">
          <RouterLink class="job-link" to="/processing-debug?tab=jobs">
            <PipelineNode
              :label="row.job.name"
              :subtitle="row.jobsSubtitle"
              :state="row.jobState"
              :badge-completed="row.job.completed"
              :badge-total="row.job.total"
              :show-expand="row.showJobExpand"
              :is-job="true"
            />
          </RouterLink>
        </template>
        <template v-else>
          <PipelineNode is-placeholder />
        </template>
      </div>
    </section>
  </section>
</template>

<style scoped>
.pipeline-view {
  display: grid;
  gap: 0.5rem;
}

.pipeline-note {
  margin-top: 0;
  color: #555;
}

.status {
  margin: 0;
}

.status--error {
  color: #b91c1c;
}

.pipeline-board {
  display: grid;
  gap: 0.5rem;
  border: 1px solid #000;
  padding: 0.75rem;
  background: #fff;
  overflow-x: auto;
}

.pipeline-header,
.pipeline-row {
  display: grid;
  grid-template-columns:
    minmax(210px, 1fr)
    minmax(110px, 110px)
    minmax(210px, 1fr)
    minmax(110px, 110px)
    minmax(210px, 1fr)
    minmax(110px, 110px)
    minmax(210px, 1fr);
  gap: 0.5rem;
  align-items: center;
  min-width: 1220px;
}

.pipeline-header {
  letter-spacing: 0.08rem;
  font-weight: 700;
}

.divider {
  height: 1px;
  background: #bdbdbd;
}

.connector-cell {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 0.35rem;
}

.connector-line {
  height: 2px;
  background: #000;
}

.connector-label {
  font-size: 0.75rem;
  line-height: 1;
  border: 1px solid #000;
  border-radius: 2px;
  background: linear-gradient(
    135deg,
    #ffffff calc(100% - var(--progress, 0%)),
    #e7f7e7 calc(100% - var(--progress, 0%))
  );
  padding: 0.2rem 0.4rem;
  white-space: nowrap;
}

.connector-arrow {
  position: relative;
  height: 2px;
  background: #000;
}

.connector-arrow::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  border-left: 6px solid #000;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.job-link {
  color: inherit;
  text-decoration: none;
}
</style>
